import { useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { differenceInDays } from 'date-fns/differenceInDays';
import { differenceInHours } from 'date-fns/differenceInHours';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { useSupportUsBannerAdState } from 'state/useSupportUsBannerAdState';
import { useAuthState } from 'state/useAuthState';
import { useOnboardingState } from 'state/useOnboardingState';
import { getLastPushNotificationTimestamp } from 'state/localStorage';

type UseWildfireBannerProps = {
  wildfire?: Wildfire;
};

type UseWildfireBanner = {
  showBanner: boolean;
  handleClose: () => void;
};

const TWO_DAY_MS = 1000 * 60 * 60 * 24 * 2; // 2 days
const FIVE_MINUTES_MS = 1000 * 60 * 5; // 5 minutes
const MIN_APP_USAGE_HOURS = 48;

export const useWildfireBanner = (
  props: UseWildfireBannerProps,
): UseWildfireBanner => {
  const { wildfire } = props;
  const { isSupporter, showSupportUs } = useAuthState();
  const { combinedOnboardedAt } = useOnboardingState();
  const {
    getGeoEventBannerLastShownTimestamp,
    updateGeoEventBannerLastShownTimestamp,
  } = useSupportUsBannerAdState();

  const lastShownTimestamp = wildfire
    ? getGeoEventBannerLastShownTimestamp(wildfire.id)
    : null;
  const lastPushNotificationTimestamp = getLastPushNotificationTimestamp();

  const getShowBanner = (): boolean => {
    if (!wildfire || isSupporter || !showSupportUs) {
      return false;
    }

    if (!Capacitor.isNativePlatform()) {
      if (!combinedOnboardedAt) {
        return false;
      }

      const appUsageInHours = Math.abs(
        differenceInHours(new Date(), new Date(combinedOnboardedAt), {
          roundingMethod: 'round',
        }),
      );

      if (appUsageInHours < MIN_APP_USAGE_HOURS) {
        return false;
      }
    }

    if (lastShownTimestamp) {
      const daysDifference = Math.abs(
        differenceInDays(new Date(), new Date(lastShownTimestamp)),
      );

      if (daysDifference < 7) {
        return false;
      }
    }

    if (!wildfire.data.isPrescribed) {
      const createdAtMs = new Date(wildfire.dateCreated).getTime();

      const createdLessThan2DaysAgo = Date.now() - createdAtMs < TWO_DAY_MS;

      if (
        createdLessThan2DaysAgo &&
        (wildfire.data.containment ?? 0) <= 40 &&
        !wildfire.data.isFps
      ) {
        return false;
      }

      if (Capacitor.isNativePlatform()) {
        const pushNotifInLast5Mins =
          Date.now() - (lastPushNotificationTimestamp || 0) <= FIVE_MINUTES_MS;

        if (pushNotifInLast5Mins) {
          return false;
        }
      }
    }

    return true;
  };

  const handleClose = useCallback((): void => {
    if (!wildfire) {
      return;
    }

    updateGeoEventBannerLastShownTimestamp(wildfire.id);
  }, [wildfire, updateGeoEventBannerLastShownTimestamp]);

  return { showBanner: getShowBanner(), handleClose };
};
